import React, { useState } from 'react'
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Tooltip,
} from '@mui/material'
import {
  Close as CloseIcon,
  ExpandMore,
  ContentCopy,
  Gavel,
  Description,
  AccountBalance,
  TextSnippet,
} from '@mui/icons-material'

interface Snippet {
  id: string
  label: string
  text: string
  category: string
}

const LEGAL_SNIPPETS: { category: string; icon: React.ReactNode; items: Snippet[] }[] = [
  {
    category: 'Endereçamento',
    icon: <AccountBalance sx={{ fontSize: 16 }} />,
    items: [
      { id: 'end-1', label: 'Juiz de Direito', category: 'end', text: 'EXCELENTÍSSIMO(A) SENHOR(A) DOUTOR(A) JUIZ(A) DE DIREITO DA _ª VARA ____ DA COMARCA DE ____/__' },
      { id: 'end-2', label: 'Juiz Federal', category: 'end', text: 'EXCELENTÍSSIMO(A) SENHOR(A) DOUTOR(A) JUIZ(A) FEDERAL DA _ª VARA FEDERAL DA SEÇÃO JUDICIÁRIA DE ____/__' },
      { id: 'end-3', label: 'Juiz do Trabalho', category: 'end', text: 'EXCELENTÍSSIMO(A) SENHOR(A) DOUTOR(A) JUIZ(A) DO TRABALHO DA _ª VARA DO TRABALHO DE ____/__' },
      { id: 'end-4', label: 'Tribunal de Justiça', category: 'end', text: 'EGRÉGIO TRIBUNAL DE JUSTIÇA DO ESTADO DE ____\nCOLENDA _ª CÂMARA DE DIREITO ____' },
      { id: 'end-5', label: 'STJ', category: 'end', text: 'EXCELENTÍSSIMO(A) SENHOR(A) MINISTRO(A) PRESIDENTE DO SUPERIOR TRIBUNAL DE JUSTIÇA' },
      { id: 'end-6', label: 'STF', category: 'end', text: 'EXCELENTÍSSIMO(A) SENHOR(A) MINISTRO(A) PRESIDENTE DO SUPREMO TRIBUNAL FEDERAL' },
      { id: 'end-7', label: 'Delegacia', category: 'end', text: 'ILUSTRÍSSIMO(A) SENHOR(A) DELEGADO(A) DE POLÍCIA DO _º DISTRITO POLICIAL DE ____/__' },
    ],
  },
  {
    category: 'Seções do documento',
    icon: <Description sx={{ fontSize: 16 }} />,
    items: [
      { id: 'sec-1', label: 'DOS FATOS', category: 'sec', text: 'I – DOS FATOS\n\n' },
      { id: 'sec-2', label: 'DO DIREITO', category: 'sec', text: 'II – DO DIREITO\n\n' },
      { id: 'sec-3', label: 'DA TUTELA DE URGÊNCIA', category: 'sec', text: 'III – DA TUTELA DE URGÊNCIA\n\nPresentes os requisitos do art. 300 do CPC, quais sejam, a probabilidade do direito e o perigo de dano ou risco ao resultado útil do processo, requer-se a concessão de tutela de urgência para ' },
      { id: 'sec-4', label: 'DOS PEDIDOS', category: 'sec', text: 'IV – DOS PEDIDOS\n\nAnte o exposto, requer-se:\n\na) \nb) \nc) ' },
      { id: 'sec-5', label: 'DAS PROVAS', category: 'sec', text: 'V – DAS PROVAS\n\nProtesta provar o alegado por todos os meios de prova em direito admitidos, especialmente documental, testemunhal e pericial, sem exclusão de quaisquer outros.' },
      { id: 'sec-6', label: 'DA LEGITIMIDADE', category: 'sec', text: 'II – DA LEGITIMIDADE\n\n' },
      { id: 'sec-7', label: 'DA COMPETÊNCIA', category: 'sec', text: 'III – DA COMPETÊNCIA\n\n' },
      { id: 'sec-8', label: 'DO VALOR DA CAUSA', category: 'sec', text: 'Dá-se à causa o valor de R$ ____,__ (____).' },
    ],
  },
  {
    category: 'Fechamentos',
    icon: <TextSnippet sx={{ fontSize: 16 }} />,
    items: [
      { id: 'close-1', label: 'Pede deferimento', category: 'close', text: 'Nestes termos,\npede deferimento.' },
      { id: 'close-2', label: 'Termos em que P.D.', category: 'close', text: 'Termos em que,\nPede Deferimento.' },
      { id: 'close-3', label: 'Aguarda deferimento', category: 'close', text: 'Ante o exposto, aguarda deferimento.' },
      { id: 'close-4', label: 'Espera provimento', category: 'close', text: 'Ante o exposto, espera e requer provimento do presente recurso.' },
      { id: 'close-5', label: 'Justiça Gratuita', category: 'close', text: 'Requer, ainda, os benefícios da Justiça Gratuita, nos termos do art. 98 do CPC, por não possuir condições de arcar com as custas processuais e honorários advocatícios sem prejuízo do próprio sustento ou de sua família.' },
    ],
  },
  {
    category: 'Expressões latinas',
    icon: <Gavel sx={{ fontSize: 16 }} />,
    items: [
      { id: 'lat-1', label: 'data venia', category: 'lat', text: 'data venia' },
      { id: 'lat-2', label: 'ad argumentandum tantum', category: 'lat', text: 'ad argumentandum tantum' },
      { id: 'lat-3', label: 'in casu', category: 'lat', text: 'in casu' },
      { id: 'lat-4', label: 'ex vi legis', category: 'lat', text: 'ex vi legis' },
      { id: 'lat-5', label: 'ab initio', category: 'lat', text: 'ab initio' },
      { id: 'lat-6', label: 'in fine', category: 'lat', text: 'in fine' },
      { id: 'lat-7', label: 'mutatis mutandis', category: 'lat', text: 'mutatis mutandis' },
      { id: 'lat-8', label: 'pacta sunt servanda', category: 'lat', text: 'pacta sunt servanda' },
      { id: 'lat-9', label: 'stare decisis', category: 'lat', text: 'stare decisis' },
      { id: 'lat-10', label: 'prima facie', category: 'lat', text: 'prima facie' },
      { id: 'lat-11', label: 'ipso facto', category: 'lat', text: 'ipso facto' },
      { id: 'lat-12', label: 'habeas corpus', category: 'lat', text: 'habeas corpus' },
    ],
  },
  {
    category: 'Fundamentação',
    icon: <Gavel sx={{ fontSize: 16 }} />,
    items: [
      { id: 'fund-1', label: 'Conforme artigo', category: 'fund', text: 'Conforme o art. ___ do ___, ' },
      { id: 'fund-2', label: 'CF/88', category: 'fund', text: 'Nos termos do art. ___ da Constituição Federal de 1988, ' },
      { id: 'fund-3', label: 'CPC', category: 'fund', text: 'Nos termos do art. ___ do Código de Processo Civil (Lei nº 13.105/2015), ' },
      { id: 'fund-4', label: 'CC', category: 'fund', text: 'Nos termos do art. ___ do Código Civil (Lei nº 10.406/2002), ' },
      { id: 'fund-5', label: 'CLT', category: 'fund', text: 'Nos termos do art. ___ da Consolidação das Leis do Trabalho, ' },
      { id: 'fund-6', label: 'CDC', category: 'fund', text: 'Nos termos do art. ___ do Código de Defesa do Consumidor (Lei nº 8.078/1990), ' },
      { id: 'fund-7', label: 'Jurisprudência pacífica', category: 'fund', text: 'A jurisprudência do ___ é pacífica no sentido de que ' },
      { id: 'fund-8', label: 'Súmula', category: 'fund', text: 'Nesse sentido, a Súmula nº ___ do ___ estabelece que: "___".' },
    ],
  },
]

interface LegalSnippetsPanelProps {
  isOpen: boolean
  onClose: () => void
  onInsert: (text: string) => void
}

const LegalSnippetsPanel: React.FC<LegalSnippetsPanelProps> = ({ isOpen, onClose, onInsert }) => {
  const [search, setSearch] = useState('')
  const [expandedCategory, setExpandedCategory] = useState<string | false>('Endereçamento')

  if (!isOpen) return null

  const filteredGroups = search.trim()
    ? LEGAL_SNIPPETS.map(g => ({
        ...g,
        items: g.items.filter(s =>
          s.label.toLowerCase().includes(search.toLowerCase()) ||
          s.text.toLowerCase().includes(search.toLowerCase())
        ),
      })).filter(g => g.items.length > 0)
    : LEGAL_SNIPPETS

  return (
    <Box sx={{
      width: 300,
      flexShrink: 0,
      borderLeft: '1px solid',
      borderColor: 'divider',
      backgroundColor: '#fafafa',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontSize: 13, fontWeight: 600, color: '#1a365d' }}>
          Frases Jurídicas
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Box sx={{ px: 1, py: 0.75 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Buscar frases..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.75 } }}
        />
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {filteredGroups.map(group => (
          <Accordion
            key={group.category}
            expanded={expandedCategory === group.category}
            onChange={(_, expanded) => setExpandedCategory(expanded ? group.category : false)}
            disableGutters
            elevation={0}
            sx={{ '&:before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <AccordionSummary expandIcon={<ExpandMore sx={{ fontSize: 16 }} />}
              sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5, gap: 0.75, alignItems: 'center' } }}>
              {group.icon}
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 11.5 }}>{group.category}</Typography>
              <Chip label={group.items.length} size="small" sx={{ height: 16, fontSize: '0.6rem', ml: 'auto', mr: 1 }} />
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1, py: 0.5 }}>
              {group.items.map(snippet => (
                <Tooltip key={snippet.id} title={snippet.text.substring(0, 120) + (snippet.text.length > 120 ? '...' : '')} arrow placement="left">
                  <Box
                    onClick={() => onInsert(snippet.text)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.06)' },
                    }}
                  >
                    <Typography variant="caption" sx={{ fontSize: 11.5 }}>{snippet.label}</Typography>
                    <ContentCopy sx={{ fontSize: 12, color: 'text.secondary', opacity: 0.5 }} />
                  </Box>
                </Tooltip>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  )
}

export default LegalSnippetsPanel
